import React, { useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Box, ThemeUICSSObject, getVariant } from '@apia/theme';
import { useAppSelector } from '../../../../../hooks/hooks';
import useContext from '../../../../../hooks/useContext';
import { arrayOrArray } from '../../../../../utils/util';
import ResponsiveTable from '../../../../common/generic/responsiveTable';
import ResponsiveTableContext from '../../../../common/generic/responsiveTable/ResponsiveTableContext';
import {
  TResponsiveTableColumn,
  TResponsiveTableRow,
  TResponsiveTableCell,
} from '../../../../common/generic/responsiveTable/types';
import useApiaFieldCommonProps from '../useApiaFieldCommonProps';
import { DataGridIdentifier } from './context';
import { IconButton } from '@apia/components';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock';

const QueryGridRenderer = () => {
  const { gridId } = useContext(DataGridIdentifier);
  const { context, cssClass, hasEditionForm } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    const { gridHeight } = state.context.grid.field.properties;
    const grid = state.context.grid.field;

    return {
      context: global.dataGridSlice[gridId].context.grid,
      height: gridHeight ? `${gridHeight}px` : '360px',
      cssClass: state.context.grid.field.properties.cssClass,
      hasEditionForm: !!grid.properties.gridForm,
    };
  }, shallowEqual);

  const columns: TResponsiveTableColumn[] = React.useMemo(() => {
    const additionalColumn: TResponsiveTableColumn[] = [];
    if (hasEditionForm) {
      additionalColumn.push({
        label: '',
        name: 'datagrid__additional',
        allowSorting: false,
        width: 30,
      });
    }

    return [
      ...additionalColumn,
      ...arrayOrArray(context.field.gridHeader.col).map((current) => {
        const newColumn: TResponsiveTableColumn = {
          name: current.title || current.colTitle,
          label: current.colTitle,
          title: current.colTitle,
          width: 130,
        };
        return newColumn;
      }),
    ];
  }, [context.field.gridHeader.col, hasEditionForm]);

  const cellsColumns = React.useMemo(() => {
    return arrayOrArray(context.field.field).map((column) => {
      const values = arrayOrArray(column.fieldValue).map((cell) => {
        return {
          children: cell.value ?? '',
        } as TResponsiveTableCell;
      });
      return values;
    });
  }, [context.field.field]);
  const rows: TResponsiveTableRow[] =
    cellsColumns[0]?.map((currentCell, rowIndex) => {
      const row: TResponsiveTableRow = {
        id: String(rowIndex),
        cells: [currentCell],
      };

      if (hasEditionForm) {
        row.cells.unshift({
          Renderer: () => (
            <td style={{ verticalAlign: 'center' }}>
              <IconButton
                style={{ margin: 'auto' }}
                icon="Edit"
                onClick={() => {
                  store.dispatch(
                    dataGridActions.update({
                      id: gridId,
                      editionFormRow: rowIndex,
                    }),
                  );
                }}
                size="Md"
              />
            </td>
          ),
        });
      }

      cellsColumns
        .slice(1)
        .forEach((otherColumn) => row.cells.push(otherColumn[rowIndex]));
      return row;
    }) ?? [];

  const { identifier } = useApiaFieldCommonProps();
  const [fullScreen, setFullScreen] = useState(false);

  const fullScreenSx: ThemeUICSSObject = fullScreen
    ? {
        position: 'fixed',
        zIndex: 'menu',
        inset: 0,

        '.footer': {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        },

        '.responsiveTable__wrapper': {
          overflow: 'auto',
          maxHeight: 'calc(100vh - 40px)',
        },
      }
    : {
        '.footer': {
          position: 'sticky',
          bottom: 0,
        },

        '.responsiveTable__wrapper': {
          overflow: 'auto',
          maxHeight: '90vh',
        },
      };

  useBodyScrollLock(fullScreen);

  return (
    <Box
      {...identifier}
      sx={fullScreenSx}
      {...window.getVariant('layout.execution.form.fields.dataGrid')}
      className={`${cssClass as string} grid__queryGrid`}
    >
      <ResponsiveTableContext
        columns={columns}
        label={context.field.properties.gridTitle}
        rows={rows}
      >
        <ResponsiveTable />
      </ResponsiveTableContext>
      <Box
        className="footer"
        {...getVariant('layout.common.components.pagination.datagrid')}
      >
        <IconButton
          icon={fullScreen ? 'Minimize' : 'Maximize'}
          onClick={() => setFullScreen((f) => !f)}
          size="Md"
        />
      </Box>
    </Box>
  );
};

export default QueryGridRenderer;
